/**
 * @file lib/payRange.js
 * @description The one parser for a case's pay. The cases table stores only `pay_min` (number) and `pay_range`
 *   (display string, "$20–30/hr" or "$48k–58k/yr"), so every reader turns the string back into numbers here:
 *   the posting wizard and edit form (parsePayRange, re-exported by caseService) and the Insights money math
 *   (payMidpoint, used by caseEconomics). Pure, no I/O.
 * @module lib/payRange
 */

/**
 * @description Reconstruct the wizard's pay fields (payMin / payMax / payPeriod)
 *   from what the DB actually persists. The cases table stores only `pay_min`
 *   (number) and `pay_range` (display string) — there is no pay_max/pay_period
 *   column — so the edit form has to parse them back out of the range string.
 *   Handles both hourly ("$20–30/hr") and annual ("$48k–58k/yr") formats and
 *   the en-dash, hyphen, or em-dash separators.
 * @param {string} payRange
 * @param {number|null} payMinCol - the stored pay_min column, used as a fallback
 * @returns {{payMin:string, payMax:string, payPeriod:string}}
 */
export function parsePayRange(payRange, payMinCol = null) {
  const raw = String(payRange || "");
  const annual = /\/yr|\bk\b|k[\s/–—-]/i.test(raw);
  const payPeriod = annual ? "annual" : "hourly";
  // Pull the numeric endpoints, multiplying "k" shorthand back to thousands.
  const nums = [];
  const re = /\$?\s*([\d.]+)\s*(k)?/gi;
  let m;
  while ((m = re.exec(raw)) !== null) {
    let n = parseFloat(m[1]);
    if (!Number.isFinite(n)) continue;
    if (m[2]) n *= 1000;                       // "48k" → 48000
    if (annual && n < 1000) n *= 1000;         // bare annual "48" → 48000
    nums.push(n);
  }
  const toField = (n) => {
    if (!Number.isFinite(n)) return "";
    // Annual fields are entered in $k/yr in the wizard; hourly in raw dollars.
    return annual ? String(Math.round(n / 1000)) : String(n);
  };
  let payMin = nums.length ? toField(nums[0]) : "";
  const payMax = nums.length > 1 ? toField(nums[1]) : "";
  // Fall back to the pay_min column when the range string had no parsable min.
  if (!payMin && payMinCol != null && payMinCol !== "") {
    payMin = annual ? String(Math.round(Number(payMinCol) / 1000)) : String(payMinCol);
  }
  return { payMin, payMax, payPeriod };
}

/**
 * Midpoint of a pay range with its period: hourly in dollars per hour, annual in dollars per year.
 * "$30–35/hr" → { amount: 32.5, period: "hourly" }; "$70–85k/yr" → { amount: 77500, period: "annual" }.
 * @param {string} payRange
 * @returns {{ amount: number, period: "hourly"|"annual" } | null}
 */
export function payMidpoint(payRange) {
  if (!payRange || typeof payRange !== "string") return null;
  const { payMin, payMax, payPeriod } = parsePayRange(payRange);
  const nums = [payMin, payMax].map(Number).filter(n => Number.isFinite(n) && n > 0);
  if (nums.length === 0) return null;
  if (payPeriod === "annual") {
    const dollars = nums.map(n => n * 1000); // annual fields come back in $k
    const amount = dollars.length > 1 ? (dollars[0] + dollars[1]) / 2 : dollars[0];
    return amount >= 1000 ? { amount, period: "annual" } : null;
  }
  const hourly = nums.filter(n => n < 1000);
  if (hourly.length === 0) return null;
  return { amount: hourly.length > 1 ? (hourly[0] + hourly[1]) / 2 : hourly[0], period: "hourly" };
}
